import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from './AppLayout';

/** Skeleton shown in the main content area while auth resolves */
function ContentSkeleton() {
  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6 animate-pulse">
      {/* Page header skeleton */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="h-5 bg-gray-200 rounded w-48" />
          <div className="h-3 bg-gray-100 rounded w-72" />
        </div>
        <div className="h-9 bg-gray-200 rounded-lg w-28" />
      </div>

      {/* Stat cards row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
            <div className="h-3 bg-gray-100 rounded w-2/3" />
            <div className="h-7 bg-gray-200 rounded w-1/2" />
            <div className="h-2 bg-gray-100 rounded w-full" />
          </div>
        ))}
      </div>

      {/* Main content card */}
      <div className="bg-white border border-gray-100 rounded-xl p-5 space-y-4">
        <div className="h-4 bg-gray-200 rounded w-36" />
        <div className="space-y-3">
          {[90, 75, 85, 60].map((w, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-gray-200 rounded" style={{ width: `${w}%` }} />
                <div className="h-2 bg-gray-100 rounded w-1/3" />
              </div>
              <div className="h-6 w-16 bg-gray-100 rounded-full flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      {/* Second card */}
      <div className="bg-white border border-gray-100 rounded-xl p-5 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-44" />
        <div className="h-2 bg-gray-100 rounded-full w-full" />
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-gray-50 border border-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  // Show AppLayout shell with skeleton — sidebar appears immediately, no full-screen spinner flash
  if (loading) return (
    <AppLayout>
      <ContentSkeleton />
    </AppLayout>
  );

  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

export default PrivateRoute;
